import Button from "../../../components/Button/Button"
import PlusIcon from "../../../assets/images/icons/PlusIcon.svg"
import DebounceInput from "../../../components/DebouncedSearch/DebouncedSearch"
import TableFooter from "../../../components/Pagination/TableFooter"
import TasksTabTable from "../../../components/TasksTab/TasksTabTable"
import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { REQUEST_ERRORS_LISTS } from "../../../constants/requestErrorFlags"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import { getListCache, setListCache, clearListCache, buildCacheKey } from "../../../utils/listCache"
import { getAllTasksPageReq } from "../../../features/tasks/tasks"
import { StorageGetItem, USER } from "../../../lib/storage"
import { initQueryData } from "../../../components/Filters/helper"
import Filters from "../../../components/Filters/Filters"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { TABLE_CARD } from "../../../components/Table/tableCardClass"
import { selectFeatureFlag } from "../../../features/featureFlags/selectors"
import { AFFILIATE_TASKS_UI } from "../../../config/featureFlagKeys"

const AllTasksPage = ({ setActiveTab, setTaskId }) => {
  const dispatch = useDispatch()
  const affiliateTasksOn = useSelector(selectFeatureFlag(AFFILIATE_TASKS_UI))
  const listStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_LISTS))
  const listStrictRef = useRef(listStrict)
  listStrictRef.current = listStrict
  const [tasks, setTasks] = useState([])
  const [valuePagination, setValuePagination] = useState(10)
  const [pagePagination, setPagePagination] = useState(1)
  const [searchValue, setSearchValue] = useState('')
  const [pageCount, setPageCount] = useState(0)
  const [totalCount, setTotalCount] = useState(null)
  const [isUpdated, setIsUpdated] = useState(false)
  const [reqError, setReqError] = useState('')
  const [requestFilters, setRequestFilters] = useState([initQueryData])
  const { triggerBar } = useMyContextLoading()
  const [userRole, setUserRole] = useState('')
  const hidesAddTask = affiliateTasksOn && (!userRole || userRole === 'affiliate' || userRole === 'affiliateGroupsAdmin')

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])

  useEffect(() => {
    const handleReqError = () => {
      reqError?.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  const getAllTasks = async () => {
    const cacheKey = buildCacheKey('tasks', { valuePagination, pagePagination, searchValue, requestFilters })
    const cached = getListCache(cacheKey)
    if (cached) {
      setTasks(cached.tasks)
      setPageCount(cached.pageCount)
      setTotalCount(cached.totalCount)
    }
    triggerBar(true)
    try {
      const limit = parseInt(valuePagination)
      const offset = (pagePagination - 1) * limit
      let filters = requestFilters[0]?.filterType?.length > 0 ? requestFilters : ""

      const response = await dispatch(getAllTasksPageReq({
        limit,
        offset,
        searchQuery: searchValue,
        filters
      }))

      if (response.payload && response.payload.data) {
        const fresh = {
          tasks: response?.payload?.data?.tasks,
          pageCount: response?.payload?.data?.meta?.pageCount,
          totalCount: response?.payload?.data?.meta?.total,
        }
        setTasks(fresh.tasks)
        setPageCount(fresh.pageCount)
        setTotalCount(fresh.totalCount)
        setListCache(cacheKey, fresh)
      } else if (listStrictRef.current && response?.error) {
        showSnackBar("error", typeof response.payload === 'string' ? response.payload : 'Failed to get all tasks')
      }
    } catch (error) {
      setReqError(error)
    }
    triggerBar(false)
  }

  const applyFilters = (newFilters) => {
    setRequestFilters(newFilters)
    setPagePagination(1)
  }

  useEffect(() => {
    getAllTasks()
  }, [])

  const tasksCacheGuard = useRef(true)
  useEffect(() => {
    if (tasksCacheGuard.current) {
      tasksCacheGuard.current = false
      return
    }
    clearListCache('tasks')
  }, [isUpdated])

  useEffect(() => {
    getAllTasks()
  }, [valuePagination, pagePagination, searchValue, isUpdated, requestFilters])

  useEffect(() => {
    setPagePagination(1)
  }, [valuePagination, searchValue])

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-9">
        <h5 className="text-xl font-bold text-text-primary">Tasks</h5>
        <div className="flex gap-2">
          {!hidesAddTask && (
            <Button classes="h-9 flex items-center justify-center" onClick={() => setActiveTab("addTask")}>
              <div className={"flex justify-center items-start gap-2"}>
                {
                  <img src={PlusIcon} alt="plus" />
                }
                Add Task
              </div>
            </Button>
          )}
        </div>
      </div>
      <div className="flex justify-between w-full my-8 px-5 items-center">
        <Filters isFilterList={false} fetchByFilters={applyFilters}
          requestFilters={requestFilters}
          flow="tasks" />
      </div>
      <div className={TABLE_CARD}>
        <div className="flex flex-col mx-5 my-4">
          <div className="flex justify-between">
            <DebounceInput classes="h-10" value={searchValue} setSearchValue={setSearchValue}
              placeholder="Search" />
          </div>
        </div>
        <TasksTabTable data={tasks}
          setIsUpdated={setIsUpdated}
          isUpdated={isUpdated}
          setActiveTab={setActiveTab}
          setTaskId={setTaskId}
          hideActions={userRole === 'user'}
        />
        <TableFooter page={pagePagination} limit={valuePagination} total={totalCount}
          pageCount={pageCount} setPage={setPagePagination} setLimit={setValuePagination} />
      </div>
    </div>
  )
}

export default AllTasksPage